import type { PayloadAction, Reducer } from '@reduxjs/toolkit';

import type { RequestMeta } from 'Core/actions/request';

export function fromPayload<T>(_state: T, { payload }: PayloadAction<T>): T {
  return payload;
}

function readMeta(action: unknown): Partial<RequestMeta> {
  if (action && typeof action === 'object' && 'meta' in action) {
    const { meta } = action as { meta?: unknown };
    if (meta !== null && typeof meta === 'object') {
      return meta as Partial<RequestMeta>;
    }
  }
  return {};
}

/**
 * Typed accessor for request-action metadata: callers need not restate the
 * meta shape at every reducer site. The authoritative list of flags lives on
 * `RequestMeta` in `Core/actions/request.ts`.
 */
export function getRequestMeta(action: unknown): Partial<RequestMeta> {
  return readMeta(action);
}

/**
 * Wraps a reducer so it only runs when the dispatched action's `meta.isolatedKey`
 * matches `isolatedKey` (or is absent).
 *
 * The generic is inferred from the reducer's **return type** rather than the
 * `Reducer<S>` state parameter. `combineReducers` from redux 5.x encodes its
 * preloaded state as `Partial<P>`, which in contravariant positions forces TS
 * to widen `S` to `S | Partial<P>`. Extracting `ReturnType<R>` keeps the
 * narrow slice shape that propagates through `ReduxStateType`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createIsolatedReducer<R extends Reducer<any, any, any>>(
  isolatedKey: string | null,
  reducer: R,
): Reducer<ReturnType<R>> {
  type S = ReturnType<R>;
  return (state, action) => {
    const currentState: S = state ?? reducer(undefined, { type: '__INIT_ACTION' });
    const meta = readMeta(action);
    const actionMatchesKey = 'isolatedKey' in meta ? meta.isolatedKey === isolatedKey : true;
    return actionMatchesKey ? reducer(currentState, action) : currentState;
  };
}
